import React, { useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from 'react-query';
import Form from 'components/elements/Form/form';
import Input from 'components/elements/Form/Input/input';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import DemoAccountService from 'services/hooks/demo-account-creation';
import { toast } from 'react-toastify';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';

interface DemoAccountInterface {
  accountName: string;
  adminEmail: string;
  occupantName: string;
  occupantEmail: string;
  occupantPhoneNumber: string;
  buildingName: string;
  image: string;
}

const DemoAccountSchema = yup.object().shape({
  accountName: yup.string().required('Account Name is required'),
  occupantName: yup.string().required('Occupant Name is required'),
  occupantEmail: yup.string().required('Occupant Email is required').email('Invalid email format'),
  occupantPhoneNumber: yup.string().required('Occupant Phone Number is required'),
  buildingName: yup.string().required('Building Name is required'),
  image: yup.string().url('Image must be a valid URL')
});

const DemoAutomation = () => {
  const [responseError, setResponseError] = useState('');

  const methods = useForm<DemoAccountInterface>({ resolver: yupResolver(DemoAccountSchema) });

  const mutation = useMutation(
    (data: DemoAccountInterface) => {
      const formattedData = {
        account_name: data.accountName,
        admin_email: data.adminEmail || '',
        occupant_full_name: data.occupantName,
        occupant_email: data.occupantEmail,
        occupant_phone_number: data.occupantPhoneNumber,
        building_names: data.buildingName,
        image: data.image || ''
      };
      return DemoAccountService.createDemoAccount(formattedData);
    },
    {
      onSuccess: () => {
        methods.reset();
        toast.success('Demo Account creation successful');
      },
      onError: (error) => {
        console.log(error);
        toast.console.error('Error while creating demo account');
        setResponseError('An error occurred while processing the form.');
      }
    }
  );

  const onSubmit = (data: DemoAccountInterface) => {
    mutation.mutate(data);
  };

  return mutation.isLoading ? (
    <div className="flex justify-center items-center">
      <LoadingSpinner />
    </div>
  ) : (
    <>
      <div className="flex items-center space-x-2 pb-9">
        <h1 className="pb-0">Demo Account Creation</h1>
        {
          <div className="pb-2">
            {<HelpTooltipIcon message="Use this form to create demo accounts." />}
          </div>
        }
      </div>
      <Form
        buttonLabel="Create Demo Account"
        methods={methods}
        onSubmit={methods.handleSubmit(onSubmit)}
        styles="w-full px-2.5"
        buttonAlignment="justify-center" // Button aligned to center
        buttonSize="w-80" // Medium-sized button
      >
        <Input
          label="Account Name"
          name="accountName"
          type="text"
          placeholder="Enter account name"
          autoFocus
          labelClass="!font-normal"
        />
        <Input
          label="Occupant Name"
          name="occupantName"
          type="text"
          placeholder="Enter occupant name"
          labelClass="!font-normal"
        />
        <Input
          label="Occupant Email"
          name="occupantEmail"
          type="email"
          placeholder="occupant@example.com"
          labelClass="!font-normal"
        />
        <Input
          label="Occupant Phone Number"
          name="occupantPhoneNumber"
          type="text"
          placeholder="Enter occupant phone number"
          labelClass="!font-normal"
        />
        <Input
          label="Building Names"
          name="buildingName"
          type="text"
          placeholder="Enter building names (comma-separated) i.e. Building1, Building2"
          labelClass="!font-normal"
        />
        {responseError ? <div className="text-secondary2">{responseError}</div> : null}
      </Form>
    </>
  );
};

export default DemoAutomation;
